import type { DefaultCacheLoader } from "../../core/cache/default-cache-loader";
import { logger } from "../../core/logging/logger";
import { Observable } from "../../utils/observable";
import { ObservablePaginated } from "../../utils/observable-paginated";
import { createEmptyPaginatedData } from "../../utils/pagination";
import type { FeaturesManager } from "../features/features-manager";
import type { Mandate } from "./mandate";
import type { MandatesService } from "./mandates-service";

export class MandatesManager {
  public constructor(
    private readonly mandatesService: MandatesService,
    private readonly featuresManager: FeaturesManager,
    private readonly cacheLoader?: DefaultCacheLoader<Mandate[]>,
  ) {}

  public paginatedMandates = new ObservablePaginated<Mandate>(createEmptyPaginatedData<Mandate>());

  public isLoading = new Observable<boolean>(false);
  public canLoadMore = new Observable<boolean>(false);

  public async getMandates(offset?: number) {
    const features = this.featuresManager.features.get();
    if (!features.mandateView) {
      logger.debug("MandatesManager", "User does not have permission to view mandates");
      this.paginatedMandates.set(createEmptyPaginatedData<Mandate>());
      this.canLoadMore.set(false);
      return;
    }
    this.isLoading.set(true);
    try {
      const response = await this.mandatesService.getMandates(offset);
      if (offset) {
        this.paginatedMandates.add(response);
      } else {
        this.paginatedMandates.set(response);
      }
      this.canLoadMore.set(response.count > response.limit + Number(response.offset));
    } catch (error) {
      logger.debug("MandatesManager", "Failed to get mandates", error);
      throw new Error(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async opposeMandate(id: string) {
    try {
      const response = await this.mandatesService.opposeMandate(id);

      return response;
    } catch (error) {
      logger.debug("MandatesManager", "Failed to oppose mandate", error);
      throw new Error(error);
    }
  }

  public async activateMandate(id: string) {
    try {
      const response = await this.mandatesService.activateMandate(id);

      return response;
    } catch (error) {
      logger.debug("MandatesManager", "Failed to activate mandate", error);
      throw new Error(error);
    }
  }

  public async loadMore() {
    if (this.canLoadMore.get()) {
      const paginatedMandates = this.paginatedMandates.get();
      const nextOffset = paginatedMandates.limit + Number(paginatedMandates.offset);
      await this.getMandates(nextOffset);
    }
  }
}
